import { useUser } from "@utils/store/user/useUser";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import { PracticeScenario, PRACTICE_SCENARIO_KEY } from "@utils/practice/practiceMode";
import {
  PRACTICE_SUPPLIER_ID,
  PRACTICE_ADDRESS_ID,
  PRACTICE_ADDRESS_FULL,
} from "@utils/practice/practiceOrder";
import { practiceSupplierName, practiceSupplierPhone, practiceSupplierTaxId } from "@utils/practice/practiceMerchant";

/** 需要「既有身分」才能運作的情境（下單 / 到期通知 / 首次設定 / 查訂單 / 帳號管理）。 */
export const needsIdentity = (s: PracticeScenario) =>
  s === "orderGasShop" ||
  s === "orderKiosk" ||
  s === "notifyExpiring" ||
  s === "firstJoinSetup" ||
  s === "orders" ||
  s === "manageAccount";

/** 練習沙盒的預設「已登入」身分：練習家庭帳戶 + 瓦斯行 + 地址。 */
export const practiceIdentity = () => ({
  context_id: 9001,
  context_name: "練習家庭帳戶",
  role: "owner",
  is_payer: true,
  member_count: 2,
  max_members: 6,
  primary_supplier: { supplier_id: PRACTICE_SUPPLIER_ID, supplier_name: practiceSupplierName(), tax_id_number: practiceSupplierTaxId(), is_open_now: true, phone: practiceSupplierPhone() },
  choose_address: { customer_address_id: PRACTICE_ADDRESS_ID, full_address: PRACTICE_ADDRESS_FULL },
  address: [{ customer_address_id: PRACTICE_ADDRESS_ID, full_address: PRACTICE_ADDRESS_FULL }],
});

/** 種入預設身分（讓下單 / 訂單 / 帳號 / 首頁等頁面能直接顯示 mock 資料）。 */
export const seedPracticeIdentity = () => {
  useUser.getState().setChooseIdentity(practiceIdentity() as never);
};

/**
 * 練習專用部署的開機保底：直接開任何頁（rich menu 深連結、重新整理）時，
 * 若不是「需登出」的註冊 / 綁定 / 掃碼情境，就種入預設身分，
 * 讓頁面直接有 mock 資料、不卡在「尚未選擇身分」。
 *
 * 規則（依 sessionStorage 的當前情境判斷）：
 * - 尚未選任何情境（rich menu 直接進 /home 等） → 種
 * - 目前情境需要身分（下單 / 訂單 / 帳號 / 首次設定） → 種
 * - 註冊 / 綁定 / 掃碼情境 → 不種（那些流程要從登出狀態開始）
 *
 * 註：與 startScenario 相容——deeplink 進場時 startScenario 會另外設定/清除身分，
 * 兩者最終結果一致（需身分的情境都種、註冊類都清）。
 */
export const ensurePracticeIdentity = () => {
  let scenario: string | null = null;
  try {
    scenario = sessionStorage.getItem(PRACTICE_SCENARIO_KEY);
  } catch {
    scenario = null;
  }
  if (!scenario || needsIdentity(scenario as PracticeScenario)) {
    seedPracticeIdentity();
  }
  // 直接進（無情境，例如 rich menu「一鍵下單」→ /kiosk）：視為既有已驗證客戶，
  // 略過首次結帳的手機 OTP。教學用的情境流程（/practice/orderKiosk）由 startScenario
  // 自行把 otpVerified 設回 false，保留 OTP 教學，不受此影響。
  if (!scenario) {
    useKiosk.getState().setOtpVerified(true);
  }
};
